import { Search } from "react-feather";
import { Button, CardBody, Input, Modal, ModalBody, ModalFooter, ModalHeader, Table } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useState } from "react";
import PaginationComponent from "Component/Learning/Common/Pagination";
import { setFilters } from "Features/Learning/Memberships/membershipSlice";
import { handleBackPagination, handleClickPagination, handleNextPagination } from "Component/Learning/Membership/MembershipsUtils";
import { fetchMemberships } from "Features/Learning/Memberships/Utils/MembershipUtils";
import Swal from "sweetalert2";

const staticOptions = [
  { id: 1, title: 'Global' },
  { id: 2, title: 'Is Not Verified' }
];

const ModalAddTopics = ({ handleOpenTopics, openTopics, selectedValue, handleSelectChange }) => {
  const [memberships, setMemberships] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [refatch, setRefatch] = useState(false);
  const [search, setSearch] = useState('');
  const [initialSnapshot, setInitialSnapshot] = useState([]);

  const filters = useSelector(state => state?.Membership?.filters);
  const isLoading = useSelector(state => state?.Membership?.isLoading);
  const pagination = useSelector((state) => state?.Membership?.data?.pagination);
  const dispatch = useDispatch();
  const [isSelectAll, setIsSelectAll] = useState(false);

  // Local selection state (do not mutate parent until Save)
  const [localSelectedValue, setLocalSelectedValue] = useState(selectedValue || []);

  useEffect(() => {
    // whenever modal opens, capture snapshot and sync local state
    if (openTopics) {
      const initial = selectedValue || [];
      setInitialSnapshot(initial);
      setLocalSelectedValue(initial);
      setSelectedIds((initial || []).map(item => item.id));
    }
  }, [selectedValue, openTopics]);

  useEffect(() => {
    dispatch(fetchMemberships({
      ...filters,
      'sort': 'title',
    }))
      .then((response) => {
        setMemberships(response?.payload?.data || []);
      })
      .catch((error) => {
        console.error('Error fetching options:', error);
      });
  }, [dispatch, filters, refatch]);

  useEffect(() => {
    if (memberships.length > 0 && memberships.every(item => selectedIds?.includes(item.id))) {
      setIsSelectAll(true);
    } else {
      setIsSelectAll(false);
    }
  }, [selectedIds, memberships]);

  const handleClickSearch = () => {
    setRefatch(!refatch);
  };

  const updateSearchParams = useCallback(() => {
    dispatch(
      setFilters({
        ...filters,
        "filter[title]": search,
        "page": 1
      })
    );
  }, [search, dispatch]);

  useEffect(() => {
    const delay = setTimeout(updateSearchParams, 500);
    return () => clearTimeout(delay);
  }, [search, updateSearchParams]);

  const handleSelectAll = () => {
    if (isSelectAll) {
      const idsToRemove = memberships.map(item => item.id);
      const newSelectedValue = (localSelectedValue || []).filter((value) => !idsToRemove.includes(value.id));
      const newSelectedIds = selectedIds.filter((id) => !idsToRemove.includes(id));
      setLocalSelectedValue(newSelectedValue);
      setSelectedIds(newSelectedIds);
    } else {
      const max = 5;
      const sisa = max - (localSelectedValue?.length || 0);
      if (sisa <= 0) return;
      const newItemsToAdd = memberships.filter(item => !selectedIds.includes(item.id)).slice(0, sisa);
      setLocalSelectedValue([...(localSelectedValue || []), ...newItemsToAdd]);
      setSelectedIds([...selectedIds, ...newItemsToAdd.map(item => item.id)]);
    }
  };

  const handleCheckboxChange = (item) => {
    const isStatic = staticOptions.some(opt => opt.id === item.id);
    const selectedStatic = selectedIds.find(id => staticOptions.some(opt => opt.id === id));
    if (selectedIds.includes(item.id)) {
      const newSelectedValue = (localSelectedValue || []).filter((value) => value.id !== item.id);
      const newSelectedIds = selectedIds?.filter((id) => id !== item.id);
      setLocalSelectedValue(newSelectedValue);
      setSelectedIds(newSelectedIds);
    } else {
      if (isStatic) {
        setLocalSelectedValue([item]);
        setSelectedIds([item.id]);
      } else {
        if (selectedStatic) return;
        if ((localSelectedValue || []).length >= 5) return;
        setLocalSelectedValue([...(localSelectedValue || []), item]);
        setSelectedIds([...selectedIds, item.id]);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newSelectedValue = (localSelectedValue || []).filter((value, index, self) =>
      index === self.findIndex((t) => t.id === value.id)
    );
    // notify parent (parent will dispatch setField to redux)
    handleSelectChange(newSelectedValue);
    handleOpenTopics(); // close modal
  }

  // toggle modal with confirmation when there are changes compared to initial snapshot
  const toggleModal = async () => {
    const items = Array.isArray(initialSnapshot) ? initialSnapshot : [];
    const initialIds = (items || []).map((i) => i.id).sort().join(",");
    const currentIds = (selectedIds || []).slice().sort().join(",");

    if (openTopics) {
      // closing: if changed, confirm
      if (initialIds !== currentIds) {
        const result = await Swal.fire({
          title: "Perubahan belum disimpan",
          text: "Perubahan yang belum disimpan akan dibatalkan. Lanjutkan?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          confirmButtonText: "Ya",
          cancelButtonColor: "#d33",
        });
        if (!result.isConfirmed) return;
      }

      // restore snapshot and close modal
      setLocalSelectedValue(items?.length > 0 ? items : []);
      setSelectedIds(items?.length > 0 ? items.map((it) => it.id) : []);
      handleOpenTopics();
      return;
    }

    // opening: initialize from prop selectedValue (will be captured by useEffect) and open
    handleOpenTopics();
  };

  return (
    <Modal isOpen={openTopics} size="lg" toggle={toggleModal}>
      <ModalHeader toggle={toggleModal}>
        Tambahkan Topics
      </ModalHeader>
      <ModalBody>
        <div className="mb-3">
          <strong>Terpilih:</strong> {localSelectedValue?.length || 0}
          <ul className="list-group mt-2">
            {localSelectedValue?.map(item => <li key={`selected-${item.id}`} className="list-group-item">{item.title}</li>)}
          </ul>
        </div>

        <div className="faq-form">
          <Input
            className="form-control"
            type="text"
            placeholder="Search.."
            value={search}
            onKeyUp={(e) => (e.key === "Enter" ? handleClickSearch() : null)}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Search className="search-icon" />
        </div>

        <h5 className="mt-4">Daftar Topics</h5>
        <Table className="table-responsive table-bordered">
          <tbody>
            {staticOptions.map((item) => (
              <tr key={`static-${item.id}`}>
                <td>{item.title}</td>
                <td className="text-center" style={{ width: '50px' }}>
                  <Input
                    className="checkbox_animated"
                    id={`checkbox-${item.id}`}
                    type="checkbox"
                    checked={selectedIds.includes(item.id)}
                    onChange={() => handleCheckboxChange(item)}
                    disabled={
                      (!selectedIds.includes(item.id) && (
                        (selectedIds.some(id => staticOptions.some(opt => opt.id === id) && id !== item.id)) ||
                        // disable static if ANY non-static (membership) id is selected (across pages)
                        selectedIds.some(id => !staticOptions.some(opt => opt.id === id))
                      ))
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        <h5 className="mt-4">Daftar Membership</h5>
        <Table className="table-responsive table-bordered">
          <thead className="table-light">
            <tr>
              <th scope="col">{"#"}</th>
              <th scope="col">{"ID"}</th>
              <th scope="col">Nama Memberships</th>
              <th scope="col" className="text-center" style={{ width: '50px' }}>
                <Input
                  className="checkbox_animated"
                  id={`selectAll`}
                  type="checkbox"
                  title="Pilih semua di halaman ini"
                  checked={isSelectAll}
                  disabled={
                    memberships.length === 0 ||
                    selectedIds.some(id => staticOptions.some(opt => opt.id === id))
                  }
                  onChange={handleSelectAll}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan="4" className="text-center">Loading...</td></tr>}
            {!isLoading && memberships.length === 0 && <tr><td colSpan="4" className="text-center">Data tidak ditemukan.</td></tr>}
            {!isLoading && memberships.map((item, index) => (
              <tr key={`item-${item.id}`}>
                <th>{pagination?.from + index}</th>
                <th>{item?.id}</th>
                <td>{item?.title}</td>
                <td className="text-center">
                  <Input
                    className="checkbox_animated"
                    id={`detach-${item?.id}`}
                    type="checkbox"
                    checked={selectedIds.includes(item?.id)}
                    onChange={() => handleCheckboxChange(item)}
                    disabled={
                      (!selectedIds.includes(item?.id) && selectedIds.some(id => staticOptions.some(opt => opt.id === id)))
                      || (!selectedIds.includes(item?.id) && localSelectedValue.length >= 5)
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <CardBody className="mt-3">
          {!isLoading && pagination && pagination.total > pagination.per_page && (
            <PaginationComponent
              pagination={pagination}
              handleBackPagination={() => handleBackPagination(pagination, filters, dispatch, setFilters, setRefatch)}
              handleClickPagination={(page) => handleClickPagination(page, filters, dispatch, setFilters, setRefatch)}
              handleNextPagination={() => handleNextPagination(pagination, filters, dispatch, setFilters, setRefatch)}
            />
          )}
        </CardBody>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggleModal}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSubmit}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ModalAddTopics;